import React, { useEffect, useState } from "react";

import { Box, Button, Flexbox, Label, Stack, StackRow, TableLoader } from "codekit";

import { useLazyGetMipuestoMiDiaQuery } from "../../libs/redux/slices/Mipuesto/mipuestoApiSlice";
import { useNavigateProcess } from "../../utils/hooks/useNavigateProcess";

import Miescrix from "./Miescrix";

//Color de la franja: comunica urgencia sin leer.
const estiloFranja = {
  DESTRABA: { color: "error-600", fondo: "error-100" },
  RIESGO: { color: "warning-600", fondo: "warning-100" },
  COLA: { color: "primary-700", fondo: "primary-100" },
  DOCUMENTAL: { color: "neutral-600", fondo: "neutral-200" },
};

//Convierte los filtros planos del backend {CAMPO: valor} al formato de
//filtros inducidos del tracking [{CAMPO: {IN: [valor]}}]
const filtrosInducidos = (filtros) =>
  Object.entries(filtros ?? {}).map(([campo, valor]) => ({ [campo]: { IN: [valor] } }));

/**
 * Mi día: qué debo hacer hoy y en qué orden.
 *
 * La primera pantalla de Mi Puesto (FASE 5, pieza 4). Una lista priorizada
 * por franjas — destrabar a otros, riesgo, mi cola, documental — donde
 * cada elemento dice POR QUÉ está ahí y lleva de un clic a donde se
 * resuelve. Las franjas se componen por permisos: las personales nacen de
 * mis asignaciones y las de pozo común solo aparecen si tengo el módulo
 * en mi menú. Una franja sin ítems no se pinta.
 *
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-08-11
 */
const Midiaxxx = () => {
  const [franjas, setFranjas] = useState(null);
  const [getMiDia] = useLazyGetMipuestoMiDiaQuery();
  const { handleNavigateProcess } = useNavigateProcess();

  useEffect(() => {
    const handleCargar = async () => {
      await getMiDia()
        .unwrap()
        .then((response) => setFranjas(response?.data?.franjas ?? []))
        .catch(() => setFranjas([]));
    };
    handleCargar();
    const timer = setInterval(handleCargar, 60000);
    return () => clearInterval(timer);
  }, [getMiDia]);

  const handleIr = (item) => {
    const destino = item?.destino ?? {};
    if (!destino.modulo) {
      return;
    }
    handleNavigateProcess(destino.modulo, destino.proceso, filtrosInducidos(destino.filtros));
  };

  if (franjas === null) {
    return (
      <Box spacing={4}>
        <TableLoader />
      </Box>
    );
  }

  if (franjas.length === 0) {
    return (
      <Stack gap={4}>
        <Box spacing={6} rounded="md" bg="success-100">
          <Label color="success-700" weight="bold">
            Nada pendiente por hoy: no tienes autorizaciones esperando, operaciones en
            riesgo ni tareas abiertas.
          </Label>
        </Box>
        <Miescrix />
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      {/* Tiles de resumen: el tamaño del día de un vistazo */}
      <Flexbox gap={4} style={{ flexWrap: "wrap" }}>
        {franjas.map((franja) => {
          const estilo = estiloFranja[franja.clave] ?? estiloFranja.COLA;
          return (
            <Box
              key={`tile-${franja.clave}`}
              spacing={4}
              rounded="md"
              bg={estilo.fondo}
              style={{ minWidth: 170 }}
            >
              <Stack gap={1}>
                <Label color={estilo.color} weight="bold">
                  {franja.titulo}
                </Label>
                <Label weight="bold" style={{ fontSize: 26 }}>
                  {franja.total}
                </Label>
              </Stack>
            </Box>
          );
        })}
      </Flexbox>

      {franjas.map((franja) => {
        const estilo = estiloFranja[franja.clave] ?? estiloFranja.COLA;
        return (
          <Box
            key={franja.clave}
            spacing={4}
            rounded="md"
            border={1}
            borderColor="neutral-300"
            bg="white"
          >
            <Stack gap={3}>
              <Flexbox justifyContent="between" alignItems="center">
                <Label color={estilo.color} weight="bold">
                  {franja.titulo}
                </Label>
                {franja.total > franja.items.length && (
                  <Label color="neutral-600">
                    mostrando {franja.items.length} de {franja.total}
                  </Label>
                )}
              </Flexbox>
              {franja.items.map((item, indice) => (
                <Flexbox
                  key={`${franja.clave}-${indice}`}
                  justifyContent="between"
                  alignItems="center"
                  gap={4}
                >
                  <Stack gap={1}>
                    <Label weight="bold">{item.titulo}</Label>
                    <Label color="neutral-600">{item.razon}</Label>
                  </Stack>
                  {Boolean(item.destino?.modulo) && (
                    <StackRow>
                      <Button
                        type="button"
                        variant="secondary-light"
                        size="s"
                        icon="arrow-right"
                        onClick={() => handleIr(item)}
                      >
                        Ir
                      </Button>
                    </StackRow>
                  )}
                </Flexbox>
              ))}
            </Stack>
          </Box>
        );
      })}

      <Miescrix />
    </Stack>
  );
};

export default Midiaxxx;
